import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Users, AlertCircle } from "lucide-react";
import axios from "axios";
import AppFooter from "@/components/layout/AppFooter";
import AppHeader from "@/components/navigation/AppHeader";
import { getDefaultHomePath } from "@/lib/roles";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const InviteRegisterPage = () => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const { registerViaInvite, login, joinViaInvite, user, loading: authLoading } = useAuth();

  const [inviteInfo, setInviteInfo] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  const [checkingInvite, setCheckingInvite] = useState(true);

  const [authMode, setAuthMode] = useState("register");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [joining, setJoining] = useState(false);
  const [redirectingExistingMembership, setRedirectingExistingMembership] = useState(false);

  const validateInviteCode = useCallback(async () => {
    setCheckingInvite(true);
    try {
      const response = await axios.get(`${API_URL}/api/invite/${inviteCode}`);
      setInviteInfo(response.data);
      setInviteError(null);
    } catch (error) {
      setInviteError("Ungültiger oder abgelaufener Einladungs-Code");
      setInviteInfo(null);
    } finally {
      setCheckingInvite(false);
    }
  }, [inviteCode]);

  const redirectIfAlreadyInPlan = useCallback(async () => {
    setRedirectingExistingMembership(true);
    try {
      const response = await axios.get(`${API_URL}/api/dienstplan`);
      const plans = response.data || [];
      const invitePlanId = String(inviteInfo?.dienstplan_id || "").trim();
      const normalizedInviteCode = String(inviteCode || "").trim().toLowerCase();
      const matchingPlan = plans.find(
        (plan) =>
          (invitePlanId && String(plan?.id || "").trim() === invitePlanId) ||
          String(plan?.invite_code || "").trim().toLowerCase() === normalizedInviteCode
      );

      if (matchingPlan?.id) {
        localStorage.setItem("member_selected_plan_id", String(matchingPlan.id));
        navigate(getDefaultHomePath(user), { replace: true });
      }
    } catch (error) {
      console.error("Error checking existing invite membership:", error);
    } finally {
      setRedirectingExistingMembership(false);
    }
  }, [inviteInfo, inviteCode, navigate, user]);

  useEffect(() => {
    validateInviteCode();
  }, [validateInviteCode]);

  useEffect(() => {
    if (!user || checkingInvite || inviteError || !inviteInfo) return;
    redirectIfAlreadyInPlan();
  }, [user, checkingInvite, inviteError, inviteInfo, redirectIfAlreadyInPlan]);

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!name || !email || !password || !confirmPassword) {
      toast.error("Bitte fülle alle Felder aus");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Die Passwörter stimmen nicht überein");
      return;
    }

    if (password.length < 6) {
      toast.error("Das Passwort muss mindestens 6 Zeichen lang sein");
      return;
    }

    setLoading(true);
    try {
      const result = await registerViaInvite(inviteCode, name, email, password);
      toast.success(result?.message || "Registrierung erfolgreich. Bitte bestätige deine E-Mail-Adresse.");
      navigate("/login");
    } catch (error) {
      console.error("Register error:", error);
      toast.error(error.response?.data?.detail || "Registrierung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!loginEmail || !loginPassword) {
      toast.error("Bitte E-Mail und Passwort eingeben");
      return;
    }

    setLoggingIn(true);
    try {
      await login(loginEmail, loginPassword);
      setLoginPassword("");
      toast.success("Erfolgreich angemeldet");
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Anmeldung fehlgeschlagen");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleJoinDienstplan = async () => {
    setJoining(true);
    try {
      const updatedUser = await joinViaInvite(inviteCode);
      toast.success("Dienstplan beigetreten");
      navigate(getDefaultHomePath(updatedUser));
    } catch (error) {
      console.error("Join invite error:", error);
      toast.error(error.response?.data?.detail || "Beitritt fehlgeschlagen");
    } finally {
      setJoining(false);
    }
  };

  if (checkingInvite) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex flex-col" data-testid="invite-loading">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#17c671] mx-auto mb-4" />
            <p className="text-gray-600">Einladung wird geprüft...</p>
          </div>
        </div>
        <AppFooter containerClassName="max-w-5xl mx-auto px-6" />
      </div>
    );
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex flex-col" data-testid="invite-auth-loading">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#17c671] mx-auto mb-4" />
            <p className="text-gray-600">Anmeldung wird geladen...</p>
          </div>
        </div>
        <AppFooter containerClassName="max-w-5xl mx-auto px-6" />
      </div>
    );
  }

  if (redirectingExistingMembership) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex flex-col" data-testid="invite-redirecting">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#17c671] mx-auto mb-4" />
            <p className="text-gray-600">Lade deinen Dienstplan...</p>
          </div>
        </div>
        <AppFooter containerClassName="max-w-5xl mx-auto px-6" />
      </div>
    );
  }

  if (inviteError) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex flex-col" data-testid="invite-error">
        <div className="flex-1 flex items-center justify-center p-6">
          <Card className="max-w-md w-full border-gray-200 shadow-lg">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-[#212529] mb-2">Einladung ungültig</h2>
              <p className="text-gray-600 mb-6">{inviteError}</p>
              <Link to="/">
                <Button className="bg-[#17c671] hover:bg-[#14a85d] text-white font-semibold">Zur Startseite</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
        <AppFooter containerClassName="max-w-5xl mx-auto px-6" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col" data-testid="invite-register-page">
      <AppHeader
        homePath="/"
        containerClassName="max-w-md mx-auto px-6 h-16 flex items-center justify-center"
        brandClassName="font-heading font-semibold text-xl text-white"
      />

      <div className="flex-1 flex items-center justify-center p-6 pt-12">
        <div className="w-full max-w-md">
          <Card className="border-gray-200 shadow-lg mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#17c671]/10 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#17c671]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Du wurdest eingeladen zu</p>
                  <p className="font-heading font-semibold text-lg text-[#212529]">{inviteInfo?.dienstplan_name}</p>
                  <p className="text-sm text-gray-500">von {inviteInfo?.owner_name}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {user ? (
            <Card className="border-gray-200 shadow-lg">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="font-heading text-2xl text-[#212529]">Dienstplan beitreten?</CardTitle>
                <CardDescription className="text-gray-500">
                  Möchtest du dem Dienstplan "{inviteInfo?.dienstplan_name}" von {inviteInfo?.owner_name} beitreten?
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <Button
                  type="button"
                  className="w-full h-11 bg-[#17c671] hover:bg-[#14a85d] text-white"
                  onClick={handleJoinDienstplan}
                  disabled={joining}
                  data-testid="invite-join-confirm-btn"
                >
                  {joining ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Beitreten...
                    </>
                  ) : (
                    "Dienstplan beitreten"
                  )}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-gray-200 shadow-lg">
              <CardHeader className="space-y-1 pb-4">
                <div className="flex items-center gap-2 rounded-full bg-gray-100 p-1">
                  <button
                    type="button"
                    onClick={() => setAuthMode("register")}
                    className={`flex-1 rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                      authMode === "register" ? "bg-white text-[#212529] shadow-sm" : "text-gray-600"
                    }`}
                    data-testid="invite-mode-register"
                  >
                    Registrieren
                  </button>
                  <button
                    type="button"
                    onClick={() => setAuthMode("login")}
                    className={`flex-1 rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                      authMode === "login" ? "bg-white text-[#212529] shadow-sm" : "text-gray-600"
                    }`}
                    data-testid="invite-mode-login"
                  >
                    Anmelden
                  </button>
                </div>
                <CardTitle className="font-heading text-2xl text-[#212529]">
                  {authMode === "register" ? "Registrieren" : "Anmelden"}
                </CardTitle>
                <CardDescription className="text-gray-500">
                  {authMode === "register"
                    ? "Erstelle dein Konto, um dem Team beizutreten"
                    : "Melde dich an, um dem Dienstplan beizutreten"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {authMode === "register" ? (
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[#212529]">Name</Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Dein Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="h-11 border-gray-300 focus:border-[#17c671] focus:ring-[#17c671]/20"
                        data-testid="invite-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#212529]">E-Mail</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="deine@email.de"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="h-11 border-gray-300 focus:border-[#17c671] focus:ring-[#17c671]/20"
                        data-testid="invite-email-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-[#212529]">Passwort</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="********"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="h-11 pr-10 border-gray-300 focus:border-[#17c671] focus:ring-[#17c671]/20"
                          data-testid="invite-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-[#212529]">Passwort bestaetigen</Label>
                      <Input
                        id="confirmPassword"
                        type={showPassword ? "text" : "password"}
                        placeholder="********"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="h-11 border-gray-300 focus:border-[#17c671] focus:ring-[#17c671]/20"
                        data-testid="invite-confirm-password-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-11 bg-[#17c671] hover:bg-[#14a85d] text-white font-semibold btn-active"
                      disabled={loading}
                      data-testid="invite-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Registrieren...
                        </>
                      ) : (
                        "Team beitreten"
                      )}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email" className="text-[#212529]">E-Mail</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="deine@email.de"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        className="h-11 border-gray-300 focus:border-[#17c671] focus:ring-[#17c671]/20"
                        data-testid="invite-login-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password" className="text-[#212529]">Passwort</Label>
                      <div className="relative">
                        <Input
                          id="login-password"
                          type={showPassword ? "text" : "password"}
                          placeholder="********"
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          className="h-11 pr-10 border-gray-300 focus:border-[#17c671] focus:ring-[#17c671]/20"
                          data-testid="invite-login-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full h-11 bg-[#17c671] hover:bg-[#14a85d] text-white font-semibold btn-active"
                      disabled={loggingIn}
                      data-testid="invite-login-submit-btn"
                    >
                      {loggingIn ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Anmelden...
                        </>
                      ) : (
                        "Anmelden und beitreten"
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <AppFooter containerClassName="max-w-5xl mx-auto px-6" />
    </div>
  );
};

export default InviteRegisterPage;

